import React, { useState, useEffect } from "react";
import { 
  MdBusiness, 
  MdFingerprint, 
  MdCheck, 
  MdShield, 
  MdHistory, 
  MdAdd, 
  MdTimer, 
  MdFlashOn, 
  MdLayers, 
  MdSecurity,
  MdInfo,
  MdBugReport,
  MdDescription,
  MdTimeline,
  MdWebAsset,
  MdLock,
  MdDownload,
  MdLaunch,
  MdFilterList,
  MdCalendarToday,
  MdRefresh,
  MdAssignment,
  MdCheckCircle,
  MdWarning,
  MdAutorenew,
  MdClose
} from "react-icons/md";
import { Company, updateCompany, Subscription, getCompanySubscriptions } from "../../utils/api/endpoints/companies";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SubscriptionManager from "./SubscriptionManager";
import axios from "../../utils/api/axios";
import moment from "moment";

interface CompanySubscriptionTabProps {
  company: Company;
  programs?: any[];
  reports?: any[];
  pentestReports?: any[];
  companyRuns?: any[];
  assets?: any[];
  vaults?: any[];
  onUpdate?: () => void;
}

const CompanySubscriptionTab: React.FC<CompanySubscriptionTabProps> = ({
  company,
  programs: initialPrograms,
  reports: initialReports,
  pentestReports: initialPentestReports,
  companyRuns: initialCompanyRuns,
  assets: initialAssets,
  vaults: initialVaults,
  onUpdate
}) => {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState(company.company_name);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(company.company_logo || null);
  const [saving, setSaving] = useState(false);

  // Subscriptions & Linked Data State
  const [subscriptionsHistory, setSubscriptionsHistory] = useState<Subscription[]>([]);
  const [selectedTermId, setSelectedTermId] = useState<string>("active");
  const [activeLinkedTab, setActiveLinkedTab] = useState<"programs" | "reports" | "retests" | "pentest-reports" | "runs" | "assets">("programs");

  const [programs, setPrograms] = useState<any[]>(initialPrograms || []);
  const [reports, setReports] = useState<any[]>(initialReports || []);
  const [retests, setRetests] = useState<any[]>([]);
  const [pentestReports, setPentestReports] = useState<any[]>(initialPentestReports || []);
  const [companyRuns, setCompanyRuns] = useState<any[]>(initialCompanyRuns || []);
  const [assets, setAssets] = useState<any[]>(initialAssets || []);
  const [vaults, setVaults] = useState<any[]>(initialVaults || []);
  const [officialQuota, setOfficialQuota] = useState<{ quick?: any; comprehensive?: any } | null>(null);
  const [loadingLinkedData, setLoadingLinkedData] = useState<boolean>(false);

  // Fetch subscription terms & linked components if missing
  const fetchLinkedData = async () => {
    setLoadingLinkedData(true);
    try {
      const [subsRes, progsRes, reportsRes, retestsRes, pentestRes, runsRes, assetsRes, quotaRes] = await Promise.allSettled([
        getCompanySubscriptions(company.company_id),
        axios.get("/v2/programs", { params: { limit: 1000 } }),
        axios.get("/v2/reports", { params: { company_id: company.company_id, pageCount: 1000 } }),
        axios.get("/v2/retests/list"),
        axios.get("/v2/automated-report", { params: { company_id: company.company_id } }),
        axios.get("/v2/program-runs", { params: { company_id: company.company_id } }),
        axios.get("/v2/assets", { params: { company_id: company.company_id } }),
        axios.get("/v2/program-runs/quota", { params: { company_id: company.company_id } })
      ]);

      if (subsRes.status === "fulfilled" && Array.isArray(subsRes.value)) {
        setSubscriptionsHistory(subsRes.value.sort((a, b) => moment(b.start_date).diff(moment(a.start_date))));
      }

      if (progsRes.status === "fulfilled" && progsRes.value.data?.programs) {
        const companyProgs = progsRes.value.data.programs.filter((p: any) => {
          const cId = Number(p.company_id || p.asset?.company_id);
          return cId === company.company_id;
        });
        setPrograms(companyProgs);
      }

      if (reportsRes.status === "fulfilled" && reportsRes.value.data?.data) {
        setReports(reportsRes.value.data.data);
      }

      if (retestsRes.status === "fulfilled" && retestsRes.value.data) {
        const allRetests = retestsRes.value.data.retests || retestsRes.value.data.data || (Array.isArray(retestsRes.value.data) ? retestsRes.value.data : []);
        setRetests(allRetests);
      }

      if (pentestRes.status === "fulfilled" && pentestRes.value.data) {
        const pReports = pentestRes.value.data.data || (Array.isArray(pentestRes.value.data) ? pentestRes.value.data : []);
        setPentestReports(pReports);
      }

      if (runsRes.status === "fulfilled" && runsRes.value.data) {
        setCompanyRuns(runsRes.value.data.data || []);
      }

      if (assetsRes.status === "fulfilled" && assetsRes.value.data) {
        setAssets(assetsRes.value.data.assets || (Array.isArray(assetsRes.value.data) ? assetsRes.value.data : []));
      }

      if (quotaRes.status === "fulfilled" && quotaRes.value.data) {
        setOfficialQuota(quotaRes.value.data);
      }
    } catch (err) {
      console.error("Error loading linked subscription components:", err);
    } finally {
      setLoadingLinkedData(false);
    }
  };

  useEffect(() => {
    fetchLinkedData();
  }, [company.company_id]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo file size must be less than 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
    setLogoFile(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyName.trim()) {
      toast.error("Company name is required");
      return;
    }

    setSaving(true);
    const toastId = toast.loading("Updating Identity...");

    try {
      const result = await updateCompany(
        company.company_id,
        companyName.trim(),
        logoFile,
        company.subscription_type || null,
        company.quick_runs_quota || null,
        company.comprehensive_runs_quota || null,
        company.quick_run_duration || null,
        company.comprehensive_run_duration || null,
        company.is_archived || null,
        company.subscription_start_date || null,
        company.subscription_end_date || null
      );

      if (result) {
        toast.success("Organization Identity Updated", { id: toastId });
        if (onUpdate) onUpdate();
        else window.location.reload();
      } else {
        toast.error("Failed to update identity", { id: toastId });
      }
    } catch (error: any) {
      console.error("Failed to update company:", error);
      toast.error(error?.message || "Failed to update identity", { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  // Selected Subscription Term Filter Logic
  const activeTerm = subscriptionsHistory.find(s => s.subscription_id.toString() === selectedTermId) || null;

  const isWithinSelectedTerm = (dateStr?: string | null) => {
    if (!selectedTermId || selectedTermId === "all") return true;
    if (!dateStr) return true;
    const targetDate = moment(dateStr);

    if (selectedTermId === "active") {
      const start = company.subscription_start_date ? moment(company.subscription_start_date) : null;
      const end = company.subscription_end_date ? moment(company.subscription_end_date) : null;
      if (start && targetDate.isBefore(start.startOf("day"))) return false;
      if (end && targetDate.isAfter(end.endOf("day"))) return false;
      return true;
    }

    if (activeTerm) {
      const start = moment(activeTerm.start_date);
      const end = moment(activeTerm.end_date);
      if (targetDate.isBefore(start.startOf("day"))) return false;
      if (targetDate.isAfter(end.endOf("day"))) return false;
      return true;
    }

    return true;
  };

  // Filtered linked components
  const filteredPrograms = programs.filter(p => isWithinSelectedTerm(p.created_at || p.createdAt));
  const filteredReports = reports.filter(r => isWithinSelectedTerm(r.created_at || r.createdAt));
  const filteredRetests = retests.filter(rt => isWithinSelectedTerm(rt.created_at || rt.createdAt));
  const filteredPentestReports = pentestReports.filter(pr => isWithinSelectedTerm(pr.created_at || pr.createdAt));
  const filteredRuns = companyRuns.filter(rn => isWithinSelectedTerm(rn.created_at || rn.createdAt || rn.start_time));

  // Quota Metrics Calculations
  const quickQuotaTotal = activeTerm ? activeTerm.quick_runs_quota : (company.quick_runs_quota || 0);
  const compQuotaTotal = activeTerm ? activeTerm.comprehensive_runs_quota : (company.comprehensive_runs_quota || 0);

  // If viewing active term and backend quota API returned cycle counts, prefer authoritative server metrics
  const quickRunsUsed = (selectedTermId === "active" && officialQuota?.quick?.used !== undefined)
    ? officialQuota.quick.used
    : filteredRuns.filter(r => r.run_type === "quick" || r.type === "quick").length;

  const compRunsUsed = (selectedTermId === "active" && officialQuota?.comprehensive?.used !== undefined)
    ? officialQuota.comprehensive.used
    : filteredRuns.filter(r => r.run_type === "comprehensive" || r.type === "comprehensive").length;

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return "N/A";
    return moment(dateStr).format("MMM D, YYYY");
  };

  const getSeverityBadge = (sev: string) => {
    const s = (sev || "").toLowerCase();
    if (s === "critical") return "bg-red-50 text-red-700 border-red-200";
    if (s === "high") return "bg-orange-50 text-orange-700 border-orange-200";
    if (s === "medium") return "bg-amber-50 text-amber-700 border-amber-200";
    if (s === "low") return "bg-blue-50 text-blue-700 border-blue-200";
    return "bg-slate-50 text-slate-700 border-slate-200";
  };

  return (
    <div className="space-y-6">
      {/* HEADER SECTION */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-xl border border-slate-200/80 shadow-xs">
        <div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-blue-600 inline-block"></span>
            <h2 className="text-xl font-bold text-slate-900">Subscription History & Linked Components</h2>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Audit subscription terms, quota usage, and inspect linked programs, reports, retests & automated scans for <span className="font-semibold text-slate-800">{company.company_name}</span>.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className={`px-3 py-1.5 rounded-lg flex items-center gap-2 border text-xs font-semibold ${company.is_archived ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
            <span className={`w-2 h-2 rounded-full ${company.is_archived ? "bg-rose-500" : "bg-emerald-500 animate-pulse"}`} />
            {company.is_archived ? "Archived Org" : "Active Subscription Org"}
          </div>
          <div className="px-3 py-1.5 bg-slate-50 text-slate-600 rounded-lg text-xs font-mono font-bold border border-slate-200">
            ID: #{company.company_id}
          </div>
        </div>
      </div>

      {/* QUICK SUMMARY METRICS GRID */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Quick Runs Quota Card */}
        <div className="p-4 bg-white rounded-xl border border-slate-200/80 shadow-xs space-y-2">
          <div className="flex justify-between items-center text-slate-500 text-xs font-semibold">
            <span className="flex items-center gap-1.5 text-emerald-600 uppercase tracking-wider text-[11px]">
              <MdFlashOn className="text-base" /> Quick Scan Quota
            </span>
            <div className="flex items-center gap-1.5">
              <span>{quickRunsUsed} / {quickQuotaTotal} Used</span>
              {quickRunsUsed > quickQuotaTotal && (
                <span className="text-[9px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-1 py-0.2 rounded" title="Runs conducted exceed the currently configured quota allowance">
                  Over Quota
                </span>
              )}
            </div>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-2xl font-bold text-slate-900">{quickQuotaTotal}</span>
            <span className="text-xs font-medium text-slate-500">Duration: {company.quick_run_duration || 15}d</span>
          </div>
          <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${quickRunsUsed > quickQuotaTotal ? "bg-rose-500" : "bg-emerald-500"}`}
              style={{ width: `${Math.min(100, quickQuotaTotal > 0 ? (quickRunsUsed / quickQuotaTotal) * 100 : 0)}%` }}
            />
          </div>
        </div>

        {/* Comprehensive Runs Quota Card */}
        <div className="p-4 bg-white rounded-xl border border-slate-200/80 shadow-xs space-y-2">
          <div className="flex justify-between items-center text-slate-500 text-xs font-semibold">
            <span className="flex items-center gap-1.5 text-blue-600 uppercase tracking-wider text-[11px]">
              <MdLayers className="text-base" /> Comp Scan Quota
            </span>
            <div className="flex items-center gap-1.5">
              <span>{compRunsUsed} / {compQuotaTotal} Used</span>
              {compRunsUsed > compQuotaTotal && (
                <span className="text-[9px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-1 py-0.2 rounded" title="Runs conducted exceed the currently configured quota allowance">
                  Over Quota
                </span>
              )}
            </div>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-2xl font-bold text-slate-900">{compQuotaTotal}</span>
            <span className="text-xs font-medium text-slate-500">Duration: {company.comprehensive_run_duration || 30}d</span>
          </div>
          <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${compRunsUsed > compQuotaTotal ? "bg-rose-500" : "bg-blue-600"}`}
              style={{ width: `${Math.min(100, compQuotaTotal > 0 ? (compRunsUsed / compQuotaTotal) * 100 : 0)}%` }}
            />
          </div>
        </div>

        {/* Subscription Validity Term */}
        <div className="p-4 bg-white rounded-xl border border-slate-200/80 shadow-xs space-y-2">
          <div className="flex justify-between items-center text-slate-500 text-xs font-semibold">
            <span className="flex items-center gap-1.5 text-purple-600 uppercase tracking-wider text-[11px]">
              <MdCalendarToday className="text-base" /> Subscription Dates
            </span>
            <span className="capitalize px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 text-[10px] font-bold border border-purple-200">
              {company.subscription_type || "Unassigned"}
            </span>
          </div>
          <div className="text-sm font-semibold text-slate-800">
            {formatDate(company.subscription_start_date)} - {formatDate(company.subscription_end_date)}
          </div>
          <p className="text-[11px] text-slate-400">Current Active Cycle Validity</p>
        </div>

        {/* 12-Mo Retention & Archival Limit */}
        <div className="p-4 bg-white rounded-xl border border-slate-200/80 shadow-xs space-y-2">
          <div className="flex justify-between items-center text-slate-500 text-xs font-semibold">
            <span className="flex items-center gap-1.5 text-amber-600 uppercase tracking-wider text-[11px]">
              <MdShield className="text-base" /> Retention Policy
            </span>
            <span className="text-[10px] text-amber-700 font-bold bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
              12 Months
            </span>
          </div>
          <div className="text-sm font-semibold text-slate-800">
            {company.subscription_end_date
              ? formatDate(moment(company.subscription_end_date).add(12, "months").toISOString())
              : "N/A"}
          </div>
          <p className="text-[11px] text-slate-400">Data Deletion / Archival Transition</p>
        </div>
      </div>

      {/* TOP ROW: BRAND IDENTITY & SUBSCRIPTION LIFECYCLE MANAGER */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT COLUMN: BRAND IDENTITY FORM */}
        <div className="lg:col-span-1">
          <motion.div 
            initial={{ opacity: 0, x: -15 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-xl border border-slate-200/80 shadow-xs overflow-hidden"
          >
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm">
                <MdFingerprint className="text-blue-600 text-lg" />
                <span>Brand Identity</span>
              </div>
            </div>

            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* LOGO UPLOAD */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-2">Organization Logo</label>
                  <div className="relative group">
                    <div className="aspect-square w-28 mx-auto rounded-xl bg-slate-50 border-2 border-dashed border-slate-300 flex flex-col items-center justify-center overflow-hidden transition-all group-hover:border-blue-400 group-hover:bg-blue-50/50 shadow-2xs">
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo" className="w-full h-full object-contain p-2" />
                      ) : (
                        <div className="flex flex-col items-center gap-1.5">
                          <MdBusiness className="text-3xl text-slate-300" />
                          <p className="text-[10px] font-medium text-slate-400">No Logo</p>
                        </div>
                      )}

                      <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" id="tab-logo-upload" />
                        <label htmlFor="tab-logo-upload" className="p-2 rounded-full bg-white text-slate-900 cursor-pointer hover:bg-slate-100 transition-colors">
                          <MdFlashOn className="text-base" />
                        </label>
                        {logoPreview && (
                          <button type="button" onClick={handleRemoveLogo} className="p-2 rounded-full bg-rose-600 text-white hover:bg-rose-700 transition-colors">
                            <MdAdd className="text-base rotate-45" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="mt-2 text-[10px] text-slate-400 text-center">SVG, PNG or JPG (Max 5MB)</p>
                </div>

                {/* NAME INPUT */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Company Name</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-slate-800 transition"
                    placeholder="Enter Company Name"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="w-full h-9 bg-blue-600 text-white rounded-lg font-semibold text-xs flex items-center justify-center gap-2 hover:bg-blue-700 transition shadow-xs disabled:opacity-50"
                  disabled={saving}
                >
                  {saving ? "Updating..." : (
                    <>
                      <MdCheck className="text-base" />
                      Update Identity
                    </>
                  )}
                </button>
              </form>
            </div>

            <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex gap-2.5 items-center">
              <MdInfo className="text-blue-600 text-base shrink-0" />
              <p className="text-[11px] text-slate-600">
                Identity updates sync automatically across all user dashboards.
              </p>
            </div>
          </motion.div>
        </div>

        {/* RIGHT COLUMN: SUBSCRIPTION MANAGER */}
        <div className="lg:col-span-2">
          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl border border-slate-200/80 shadow-xs overflow-hidden h-full flex flex-col justify-between"
          >
            <SubscriptionManager companyId={company.company_id} onSync={fetchLinkedData} />
          </motion.div>
        </div>
      </div>

      {/* BOTTOM SECTION: LINKED COMPONENTS EXPLORER */}
      <div className="bg-white rounded-xl border border-slate-200/80 shadow-xs overflow-hidden">
        {/* EXPLORER TOOLBAR & TERM SELECTOR */}
        <div className="p-4 bg-slate-50/70 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <MdFilterList className="text-blue-600 text-lg" />
            <h3 className="font-bold text-slate-900 text-sm">Subscription Linked Components Explorer</h3>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-slate-500">Filter by Term:</span>
            <select
              value={selectedTermId}
              onChange={(e) => setSelectedTermId(e.target.value)}
              className="px-3 py-1.5 text-xs font-semibold bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-slate-800 shadow-2xs transition"
            >
              <option value="active">Current Active Subscription Term</option>
              <option value="all">All Subscription Terms & History</option>
              {subscriptionsHistory.map((sub, idx) => (
                <option key={sub.subscription_id} value={sub.subscription_id.toString()}>
                  Term #{subscriptionsHistory.length - idx} ({moment(sub.start_date).format("MMM YYYY")} - {moment(sub.end_date).format("MMM YYYY")}) [{sub.subscription_type}]
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* LINKED DATA NAVIGATION TABS */}
        <div className="border-b border-slate-200 bg-white">
          <nav className="flex overflow-x-auto px-4 scrollbar-hide" aria-label="Linked Component Tabs">
            {[
              { id: "programs", label: "Programs", icon: <MdBugReport className="text-base" />, count: filteredPrograms.length },
              { id: "reports", label: "Vulnerability Reports", icon: <MdSecurity className="text-base" />, count: filteredReports.length },
              { id: "retests", label: "Retests", icon: <MdAutorenew className="text-base" />, count: filteredRetests.length },
              { id: "pentest-reports", label: "Pentest PDF Reports", icon: <MdDescription className="text-base" />, count: filteredPentestReports.length },
              { id: "runs", label: "Scan Executions & Runs", icon: <MdTimeline className="text-base" />, count: filteredRuns.length },
              { id: "assets", label: "Assets", icon: <MdWebAsset className="text-base" />, count: assets.length }
            ].map((tab) => {
              const isActive = activeLinkedTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveLinkedTab(tab.id as any)}
                  className={`
                    inline-flex items-center gap-2 border-b-2 px-4 py-3 text-xs font-semibold transition-all whitespace-nowrap
                    ${isActive
                      ? "border-blue-600 text-blue-600 bg-blue-50/40"
                      : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-800"
                    }
                  `}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold leading-none ${
                    isActive ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"
                  }`}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* TAB CONTENT VIEW */}
        <div className="p-6">
          {loadingLinkedData ? (
            <div className="p-12 text-center text-slate-500">
              <MdRefresh className="text-3xl animate-spin mx-auto mb-2 text-blue-600" />
              <p className="text-xs font-semibold text-slate-700">Loading linked components for subscription term...</p>
            </div>
          ) : (
            <>
              {/* TAB 1: PROGRAMS */}
              {activeLinkedTab === "programs" && (
                filteredPrograms.length === 0 ? (
                  <div className="p-12 text-center text-slate-400">
                    <MdBugReport className="text-4xl mx-auto mb-2 text-slate-300" />
                    <p className="text-sm font-semibold text-slate-700">No programs found for this subscription period</p>
                    <p className="text-xs text-slate-400 mt-1">Try switching the term filter dropdown to "All Subscription Terms".</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredPrograms.map((prog) => (
                      <div
                        key={prog.program_id || prog.id}
                        className="p-4 bg-slate-50/80 hover:bg-blue-50/30 border border-slate-200 rounded-xl transition shadow-2xs group flex flex-col justify-between"
                      >
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                              {prog.program_type || prog.type || "Bug Bounty"}
                            </span>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${
                              prog.is_active || prog.status === "active"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : "bg-slate-100 text-slate-600 border-slate-200"
                            }`}>
                              {prog.status || (prog.is_active ? "Active" : "Completed")}
                            </span>
                          </div>

                          <h4 className="font-bold text-slate-900 text-sm group-hover:text-blue-600 transition-colors line-clamp-1">
                            {prog.program_title || prog.title || "Untitled Program"}
                          </h4>

                          <div className="text-xs text-slate-500 space-y-1">
                            <div className="flex justify-between">
                              <span>Testing Type:</span>
                              <span className="font-medium text-slate-700">{prog.testing_type || "Pentest"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Created Date:</span>
                              <span className="font-medium text-slate-700">{formatDate(prog.created_at || prog.createdAt)}</span>
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 pt-3 border-t border-slate-200/60 flex items-center justify-between">
                          <span className="text-[11px] text-slate-400 font-mono">Prog ID: #{prog.program_id || prog.id}</span>
                          <button
                            onClick={() => navigate(`/dashboard/programs/${prog.program_id || prog.id}`)}
                            className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 group-hover:underline"
                          >
                            View Program <MdLaunch className="text-sm" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}

              {/* TAB 2: VULNERABILITY REPORTS */}
              {activeLinkedTab === "reports" && (
                filteredReports.length === 0 ? (
                  <div className="p-12 text-center text-slate-400">
                    <MdSecurity className="text-4xl mx-auto mb-2 text-slate-300" />
                    <p className="text-sm font-semibold text-slate-700">No vulnerability reports filed in this subscription term</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto border border-slate-200 rounded-xl">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200 uppercase text-[11px]">
                        <tr>
                          <th className="py-3 px-4">Report ID</th>
                          <th className="py-3 px-4">Vulnerability Title</th>
                          <th className="py-3 px-4">Severity</th>
                          <th className="py-3 px-4">Status</th>
                          <th className="py-3 px-4">Submitted Date</th>
                          <th className="py-3 px-4 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredReports.map((rep) => (
                          <tr key={rep.report_id || rep.id} className="hover:bg-slate-50/80 transition-colors">
                            <td className="py-3 px-4 font-mono font-bold text-slate-900">#{rep.report_id || rep.id}</td>
                            <td className="py-3 px-4 font-medium text-slate-800 max-w-xs truncate">{rep.report_title || rep.title || "Vulnerability Report"}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase ${getSeverityBadge(rep.severity_category || rep.severity)}`}>
                                {rep.severity_category || rep.severity || "Medium"}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200 capitalize">
                                {rep.state || rep.status || "Triaged"}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-slate-600">{formatDate(rep.created_at || rep.createdAt)}</td>
                            <td className="py-3 px-4 text-right">
                              <button
                                onClick={() => navigate(`/dashboard/reports/${rep.report_id || rep.id}`)}
                                className="text-xs font-bold text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                              >
                                View <MdLaunch />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {/* TAB 3: RETESTS */}
              {activeLinkedTab === "retests" && (
                filteredRetests.length === 0 ? (
                  <div className="p-12 text-center text-slate-400">
                    <MdAutorenew className="text-4xl mx-auto mb-2 text-slate-300" />
                    <p className="text-sm font-semibold text-slate-700">No retest requests found for this subscription term</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto border border-slate-200 rounded-xl">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200 uppercase text-[11px]">
                        <tr>
                          <th className="py-3 px-4">Retest ID</th>
                          <th className="py-3 px-4">Vulnerability / Report Title</th>
                          <th className="py-3 px-4">Retest Status</th>
                          <th className="py-3 px-4">Requested Date</th>
                          <th className="py-3 px-4 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredRetests.map((rt) => (
                          <tr key={rt.retest_id || rt.id} className="hover:bg-slate-50/80 transition-colors">
                            <td className="py-3 px-4 font-mono font-bold text-slate-900">#{rt.retest_id || rt.id}</td>
                            <td className="py-3 px-4 font-medium text-slate-800">{rt.report_title || rt.report?.report_title || `Report #${rt.report_id}`}</td>
                            <td className="py-3 px-4">
                              <span className="px-2.5 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 capitalize">
                                {rt.status || "In Progress"}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-slate-600">{formatDate(rt.created_at || rt.createdAt)}</td>
                            <td className="py-3 px-4 text-right">
                              <button
                                onClick={() => navigate(`/dashboard/retests/${rt.retest_id || rt.id}`)}
                                className="text-xs font-bold text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                              >
                                View Retest <MdLaunch />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {/* TAB 4: PENTEST PDF REPORTS */}
              {activeLinkedTab === "pentest-reports" && (
                filteredPentestReports.length === 0 ? (
                  <div className="p-12 text-center text-slate-400">
                    <MdDescription className="text-4xl mx-auto mb-2 text-slate-300" />
                    <p className="text-sm font-semibold text-slate-700">No executive pentest reports generated in this term</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredPentestReports.map((pr) => (
                      <div key={pr.report_id || pr.id} className="p-4 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 bg-rose-100 text-rose-600 rounded-lg">
                            <MdDescription className="text-2xl" />
                          </div>
                          <div>
                            <h5 className="font-bold text-slate-900 text-xs">{pr.report_title || pr.title || "Pentest Security Assessment PDF"}</h5>
                            <p className="text-[11px] text-slate-500">Generated: {formatDate(pr.created_at || pr.createdAt)}</p>
                          </div>
                        </div>

                        {pr.file_path || pr.pdf_url ? (
                          <a
                            href={pr.file_path || pr.pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-2xs transition"
                          >
                            <MdDownload className="text-base" /> PDF
                          </a>
                        ) : (
                          <span className="text-[11px] text-slate-400 italic">Processing</span>
                        )}
                      </div>
                    ))}
                  </div>
                )
              )}

              {/* TAB 5: SCAN EXECUTION RUNS */}
              {activeLinkedTab === "runs" && (
                filteredRuns.length === 0 ? (
                  <div className="p-12 text-center text-slate-400">
                    <MdTimeline className="text-4xl mx-auto mb-2 text-slate-300" />
                    <p className="text-sm font-semibold text-slate-700">No quick or comprehensive runs executed during this term</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto border border-slate-200 rounded-xl">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200 uppercase text-[11px]">
                        <tr>
                          <th className="py-3 px-4">Run ID</th>
                          <th className="py-3 px-4">Type</th>
                          <th className="py-3 px-4">Target Asset</th>
                          <th className="py-3 px-4">Status</th>
                          <th className="py-3 px-4">Duration</th>
                          <th className="py-3 px-4 text-right">Run Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredRuns.map((rn) => (
                          <tr key={rn.run_id || rn.id} className="hover:bg-slate-50/80 transition-colors">
                            <td className="py-3 px-4 font-mono font-bold text-slate-900">#{rn.run_id || rn.id}</td>
                            <td className="py-3 px-4 uppercase font-bold text-[10px]">
                              <span className={`px-2 py-0.5 rounded border ${
                                rn.run_type === "quick" || rn.type === "quick"
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : "bg-blue-50 text-blue-700 border-blue-200"
                              }`}>
                                {rn.run_type || rn.type || "Quick"}
                              </span>
                            </td>
                            <td className="py-3 px-4 font-medium text-slate-800">{rn.target || rn.asset_name || "Company Assets"}</td>
                            <td className="py-3 px-4">
                              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700 border capitalize">
                                {rn.status || "Completed"}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-slate-600">{rn.duration || "15 mins"}</td>
                            <td className="py-3 px-4 text-right text-slate-600">{formatDate(rn.start_time || rn.created_at || rn.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {/* TAB 6: ASSETS & VAULTS */}
              {activeLinkedTab === "assets" && (
                assets.length === 0 ? (
                  <div className="p-12 text-center text-slate-400">
                    <MdWebAsset className="text-4xl mx-auto mb-2 text-slate-300" />
                    <p className="text-sm font-semibold text-slate-700">No assets currently registered for this company</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {assets.map((ast) => (
                      <div key={ast.asset_id || ast.id} className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
                        <span className="text-[10px] font-mono font-bold text-blue-600">ID: #{ast.asset_id || ast.id}</span>
                        <h5 className="font-bold text-slate-900 text-xs truncate">{ast.asset_name || ast.name || ast.domain}</h5>
                        <p className="text-[11px] text-slate-500 uppercase">{ast.asset_type || ast.type || "Web Application"}</p>
                      </div>
                    ))}
                  </div>
                )
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanySubscriptionTab;
